/**
 * ESCHER FALLS — エントリポイント。全モジュールの配線とメインループ。
 * MODULE-CONTRACTS §16 / CORE-SPEC §3.1・§5。
 *
 * このファイルだけが「入力の解釈」と「モジュール間の接続」を持つ。
 * 各モジュールは互いを知らず、ここで初めて1つのアプリになる。
 *
 * 最重要の不変条件（CORE-SPEC §3.1）:
 * **接続グラフの再構築は `rotating → idle` の瞬間にのみ起きる。**
 * その規則は `game/state.ts` の中に閉じており、main は
 * `game.update(dt)` を毎フレーム呼ぶだけでよい。ここで `buildGraph` を呼んではならない。
 */

import './style.css';

import type { GamePhase, GameState, Level } from './core/types';
import { AXIS_VECTORS, computeBounds } from './core/pieces';
import { ELEVATION_ANGLES, azimuthAngle } from './core/projection';
import { createScene } from './render/scene';
import { createCameraRig, REVEAL_TOTAL_MS } from './render/cameraRig';
import { chooseRevealOffset, type RevealJoint } from './render/revealDirection';
import { buildPieceMesh, clearMeshRegistry } from './render/meshes';
import { createWaterAnimator } from './render/water';
import { createGame } from './game/state';
import { createLevelTimer } from './game/timer';
import {
  grantExtras,
  loadRecords,
  markAllCleared,
  recordClear,
  saveRecords,
  type RecordsData,
} from './game/records';
import {
  categoryOf,
  freeExtraId,
  isAllComplete,
  isMainComplete,
  mainLevelIds,
  mainProgress,
  nextPlayableIndex,
} from './game/progression';
import {
  REWARD_MAX_PER_LEVEL,
  REWARD_TIME_MS,
  isRewardedAdAvailable,
  setRewardedAdProvider,
  setAdsRemoved,
  showRewardedAd,
} from './game/rewardedAd';
import { LEVELS } from './levels';
import * as THREE from 'three';
import { createHud } from './ui/hud';
import { createStageSelect, type DailyView } from './ui/stageSelect';
import { LANG, t } from './ui/i18n';
import { requestReview } from './game/review';
import { IS_WEB_DEMO, DEMO_MAIN_LEVELS, APP_STORE_URL } from './game/demo';

/** 体験版なら本編の上限。アプリでは undefined（＝制限なし）。 */
const DEMO_LIMIT: number | undefined = IS_WEB_DEMO ? DEMO_MAIN_LEVELS : undefined;
import { isShareAvailable, shareCard } from './game/share';
import { canOpenStore, openStore, DEVELOPER_URL, WRITE_REVIEW_URL } from './game/links';
import { buildShareCard, shareText } from './game/shareCard';
import { stageNumber } from './ui/kanji';
import { levelName } from './ui/i18nLevels';
import { formatTime } from './game/timer';
import { createFinale } from './ui/finale';
import { createDebugOverlay, isDebugEnabled } from './ui/debug';
import { playSfx, startFlow, stopFlow } from './audio/sfx';
import { startBgm } from './audio/bgm';
import {
  applyVolumes,
  audioSettings,
  audioStateLabel,
  isRunning as isAudioRunning,
  onceRunning,
  bgmBusForDebug,
  seBusForDebug,
  suspendAudio,
  unlock as unlockAudio,
} from './audio/engine';
import { saveAudioSettings } from './game/settings';
import {
  AD_MULTIPLIER,
  COST_EXTEND_TIME,
  DROPS_ALL_COMPLETE,
  DROPS_MAIN_COMPLETE,
  clearReward,
  earn,
  loadWallet,
  saveWallet,
  settleRecovery,
  spend,
  type Wallet,
} from './game/wallet';
import { createSettings } from './ui/settings';
import { applyCssPalette } from './ui/theme';
import { applyPalette } from './render/materials';
import {
  loadSkins,
  saveSkins,
  skinById,
  grant as grantSkin,
  select as selectSkin,
  type SkinState,
} from './game/skins';
import {
  EXTRAS_PRODUCT_ID,
  NOADS_PRODUCT_ID,
  isStoreAvailable,
  productById,
  purchase,
  restore,
  setPurchaseProvider,
} from './game/iap';
import { registerStoreKit, restoreOwned } from './game/storekit';
import { registerAdMob } from './game/admob';
import { recordIdFor, toLoop2 } from './game/loop2';
import { fetchArchiveDates, fetchDaily, fetchUpcomingDates, isArchiveOpen, isDailyId, todayKey } from './game/daily';

// =============================================================================
// チューニング定数
// =============================================================================

/** 1フレームに渡す dt の上限 (ms)。タブ復帰時の巨大な dt でアニメが飛ぶのを防ぐ。 */
const MAX_DT_MS = 100;

/** クリアしてから次の面へ進むまでの余韻 (ms)。MODULE-CONTRACTS §16.12。 */
const CLEAR_ADVANCE_MS = 1600;

/**
 * 今回の通水で、次の面（または祝いの画面）へ移るまで待つ時間 (ms)。
 * **種明かしを見せるときだけ延ばす**——演出の途中で盤面が切り替わると台無しになる。
 * 通水のたびに `handlePhaseChange` が決め直す。
 */
let clearHoldMs = CLEAR_ADVANCE_MS;

/** 種明かしが終わって、解いた絵が落ち着くまでの余白 (ms)。 */
const REVEAL_SETTLE_MS = 200;
/** 種明かしを飛ばしたあと、次の面へ移るまでの間 (ms)。 */
const REVEAL_SKIP_ADVANCE_MS = 500;

/**
 * **種明かし**（解いた視点から少しずらし、錯視の継ぎ目が宙を隔てていることを見せる）
 * を、この通水で見せるか。
 *
 * - その面を**初めて**通したときだけ。遊び直しのたびに4秒待たされるのは邪魔である
 *   （日替わりは例外で毎回見せる）
 * - 二周目では見せない。同じ面の二度目なので、種はもう知っている
 * - 端末で「視差効果を減らす」を選んでいる人には見せない
 */
function shouldReveal(firstClear: boolean): boolean {
  // **日替わり（今日の一面・これまでの一面）は毎回見せる。** その日限りの一問で、
  // 遊び直す人も種明かしを見に戻ってくることが多い（2026-10-08 ユーザーの指摘）。
  const daily = levelIndex < 0;
  if (!daily && (!firstClear || loop >= 2)) return false;
  try {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
  } catch {
    // 調べられない環境では見せてよい。
  }
  return true;
}

/**
 * いまの通水で**水が渡った錯視の継ぎ目**を集める。種明かしの向きを選ぶ材料。
 *
 * グラフの継ぎ目のうち、両端の口がどちらも水の通り道（放水プランの各段の出入り口）に
 * 含まれ、かつ **3D では離れている**ものだけを拾う。通っていない継ぎ目や、
 * 本当に接している継ぎ目を見せても種明かしにならない。
 */
function crossedJoints(): RevealJoint[] {
  const { graph, flow } = game.state;
  if (flow === null) return [];
  const wet = new Set<string>();
  for (const st of flow.steps) {
    wet.add(st.fromPort);
    wet.add(st.toPort);
  }
  const out: RevealJoint[] = [];
  for (const link of graph.links) {
    if (!wet.has(link.a) || !wet.has(link.b)) continue;
    const pa = graph.ports.get(link.a);
    const pb = graph.ports.get(link.b);
    if (pa === undefined || pb === undefined) continue;
    // **3D でも本当に接している継ぎ目は除く。** 口どうしが同じ場所にあれば、
    // どこから見ても隙間は 0 で、種明かしの材料にならない（入れると最小値が常に 0 になり、
    // 向きを選ぶ意味が消える——実際に2〜5面でそうなった）。
    const gap = Math.hypot(pb.pos[0] - pa.pos[0], pb.pos[1] - pa.pos[1], pb.pos[2] - pa.pos[2]);
    if (gap < 0.01) continue;
    out.push({ a: pa.pos, b: pb.pos, axis: AXIS_VECTORS[pa.dir] });
  }
  return out;
}

/** 解いた視点と継ぎ目から、この面の種明かしでずらす向きを選ぶ。 */
function revealOffsetForNow(): ReturnType<typeof chooseRevealOffset> {
  const pose = game.state.pose;
  return chooseRevealOffset(
    crossedJoints(),
    azimuthAngle(pose.azimuth),
    ELEVATION_ANGLES[pose.elevation],
  );
}

/**
 * 種明かしを飛ばす。画面に触れた・キーを押したときに呼ぶ。
 * **飛ばしたら待たせない。** 次の面への予約が残っていれば早める（提案を出していて
 * 予約が無いときは何もしない——押す間もなくボタンが消えるのを避けるため）。
 * @returns 飛ばしたなら true（呼び出し側はその入力をそこで止める）。
 */
function skipReveal(): boolean {
  if (!rig.isRevealing) return false;
  rig.cancelReveal();
  if (advanceTimer !== null) {
    clearHoldMs = REVEAL_SKIP_ADVANCE_MS;
    scheduleAdvance();
  }
  return true;
}

/**
 * この試行で制限時間に足した分 (ms) と、足した回数。
 *
 * `Level.timeLimitMs` は定数なので、延長は面ごとの一時的な上乗せとして外に持つ。
 * **面を読み直したらゼロに戻す**——延長を持ち越せると、一度広告を見るだけで
 * 以降ずっと緩い制限で遊べてしまい、制限時間という仕掛けが消える。
 */
let timeBonusMs = 0;
let timeBonusCount = 0;

/** 手持ちの雫。時間回復の精算まで済ませて読み込む。 */
let wallet: Wallet = loadWallet();

/**
 * いま何周目か。二周目では手数と時間が締まり、記録も別に残る。
 * **保存しない**——次に開いたときは一周目から始める。周回は「いまの遊び方」で
 * あって進行度ではないので、覚えていると意図せず締まった状態で始まってしまう。
 */
let loop = 1;

/** 持っている配色と、着ているもの。 */
let skins: SkinState = loadSkins();

/**
 * 配色を実際に反映する。**DOM と three.js の両方に配る。**
 * 片方だけだと、盤面だけ／UI だけ着替わって気持ちの悪い状態になる。
 */
function applySkin(): void {
  const skin = skinById(skins.selected);
  applyCssPalette(skin);
  applyPalette(skin.colors);
  // 背景はマテリアルを共有していないので個別に追随させる。
  scene.setBackground(skin.colors.paper);
  // 水の見た目は `setClear(false)` を通すと色を引き直す（クリア演出の解除経路）。
  water.setClear(false);
}

/** 雫を差し替えて、保存と表示まで面倒を見る。**増減は必ずここを通す。** */
function setWallet(next: Wallet): void {
  wallet = next;
  saveWallet(wallet);
  hud.setDrops(wallet.drops);
  // 設定画面が開いていれば、買える／買えないの見え方をその場で直す。
  if (settings.isOpen) settings.refresh();
}

/**
 * この面に効いている制限時間。延長ぶんを含む。制限なしの面では null。
 *
 * **`LEVELS[levelIndex]` を見てはいけない。** 二周目は制約を載せた写しを
 * 読み込んでいるので、元の面を見ると制限が無いことになる（実際にそうなり、
 * 二周目の時間制限が丸ごと効いていなかった）。
 * 状態機械が持っている「いま読み込まれている面」を見るのが正しい。
 */
function effectiveTimeLimitMs(): number | null {
  const base = game.state.level.timeLimitMs;
  return base === null ? null : base + timeBonusMs;
}

/** 水平ドラッグを回転とみなす移動量 (px)。誤操作（タップ・縦スクロール）を弾く。 */
const DRAG_THRESHOLD_PX = 40;

// =============================================================================
// DOM とモジュールの生成
// =============================================================================

function requireElement(selector: string): HTMLElement {
  const el = document.querySelector<HTMLElement>(selector);
  if (el === null) throw new Error(`main: ${selector} が見つからない`);
  return el;
}

const app = requireElement('#app');
const uiRoot = requireElement('#ui');

const scene = createScene(app);
// bounds はレベルロード時に確定する。ここでは原点の退化 bbox（ピース0個）で初期化する。
const rig = createCameraRig(scene.camera, computeBounds([]));
const water = createWaterAnimator(scene.scene);
const game = createGame({ rotation: rig, flow: water });

const timer = createLevelTimer();
/** 記録は起動時に一度だけ読む。以後はメモリ上の値を正とし、クリア時に書き戻す。 */
let records: RecordsData = loadRecords();
const MAIN_IDS = mainLevelIds(LEVELS);

/**
 * `?unlock=1` で全ステージを選べるようにする。**動作確認専用。**
 * 記録には書き込まないので、URL から外せば本来の進行状況に戻る。
 */
const UNLOCK_ALL = new URLSearchParams(location.search).get('unlock') === '1';

// =============================================================================
// レベル進行
// =============================================================================

let levelIndex = 0;
let advanceTimer: number | null = null;

// =============================================================================
// 今日の一面（日替わり）
// =============================================================================
//
// 日替わりの面は `LEVELS` に入らない（毎日変わり、本編の進行とも課金とも無関係）。
// 遊んでいる間は `levelIndex = -1` にして、「いまの面」は {@link currentLevel} で引く。
// `LEVELS[levelIndex]` を直接見ると -1 で undefined になり、記録も面送りも黙って止まる。

/** 今日の一面。取れていなければ null。 */
let dailyLevel: Level | null = null;
let dailyStatus: 'loading' | 'ready' | 'offline' = 'loading';
/** どの日の面を持っているか。日付が変わったら取り直す。 */
let dailyDate = todayKey();

/** これまでの一面の日付（新しい順）。 */
let archiveDates: string[] = [];
/** 公開済みの先の日（近い順）。エクストラを買った人にだけ一覧へ出す。 */
let upcomingDates: string[] = [];
/**
 * 盤面に出している日替わりの面（今日のものとは限らない——これまでの一面もここ）。
 * 「やりなおす」はこれを読み直す。
 */
let activeDaily: Level | null = null;

/** いま読み込んでいる面（二周目の写しではなく元の定義）。 */
function currentLevel(): Level | undefined {
  return levelIndex >= 0 ? LEVELS[levelIndex] : activeDaily ?? undefined;
}

/** エクストラを持っているか。**持っていれば、これまでの一面がすべて開く**（案A）。 */
function extrasOwned(): boolean {
  return records.progress.extrasOwned || records.progress.unlockedExtra;
}

/** `YYYY-MM-DD` を言語に合わせた「10月8日」に。書式が使えなければそのまま。 */
function dateLabelOf(date: string): string {
  try {
    return new Intl.DateTimeFormat(LANG, { month: 'long', day: 'numeric' }).format(
      new Date(`${date}T00:00:00`),
    );
  } catch {
    return date;
  }
}

/** 記録の鍵。日替わりは周回と無関係なので、そのままの id を使う。 */
function recordKey(id: string): string {
  return isDailyId(id) ? id : recordIdFor(id, loop);
}

/** 今日の一面を取りに行く。届いたら一覧を描き直す。**失敗しても何も壊さない。** */
function loadDailyIfNeeded(): void {
  const today = todayKey();
  // 同じ日で、取れている／取りに行っている最中なら何もしない。
  // 取れなかった（通信できなかった）ときは、一覧を開くたびに取り直す。
  if (dailyFetchStarted && today === dailyDate && dailyStatus !== 'offline') return;
  dailyFetchStarted = true;
  dailyDate = today;
  dailyStatus = 'loading';
  dailyLevel = null;
  void fetchDaily(today).then((level) => {
    if (dailyDate !== today) return; // 取りに行っている間に日付が変わった
    dailyLevel = level;
    dailyStatus = level === null ? 'offline' : 'ready';
    stageSelect.refresh();
  });
  void fetchArchiveDates(today).then((dates) => {
    if (dailyDate !== today) return;
    archiveDates = dates;
    stageSelect.refresh();
  });
  void fetchUpcomingDates(today).then((dates) => {
    if (dailyDate !== today) return;
    upcomingDates = dates;
    stageSelect.refresh();
  });
}
let dailyFetchStarted = false;

/** 一覧に出す「今日の一面」と「これまでの一面」の見え方。 */
function dailyView(): DailyView {
  const owned = extrasOwned();
  const archive = archiveDates.map((date) => ({
    date,
    label: dateLabelOf(date),
    open: isArchiveOpen(date, dailyDate, owned),
    bestTimeMs: records.levels[`daily-${date}`]?.bestTimeMs ?? null,
  }));
  // 先の日は買った人だけ、明日の1面だけ。無料の人には、日付の一覧そのものを渡さない。
  const upcoming = owned
    ? upcomingDates.filter((date) => isArchiveOpen(date, dailyDate, owned)).map((date) => ({
        date,
        label: dateLabelOf(date),
        open: true,
        bestTimeMs: records.levels[`daily-${date}`]?.bestTimeMs ?? null,
      }))
    : [];
  const best = dailyLevel === null ? null : records.levels[dailyLevel.id]?.bestTimeMs ?? null;
  return {
    status: dailyStatus,
    dateLabel: dateLabelOf(dailyDate),
    bestTimeMs: best,
    archive,
    upcoming,
    archiveHasLocked: archive.some((d) => !d.open),
  };
}

/** 日替わりの面を盤面に出す。題は「今日の一面　10月8日」／「これまでの一面　10月6日」。 */
function playDailyLevel(level: Level, title: string): void {
  activeDaily = level;
  loadLevel({ ...level, name: title }, -1);
}

/** これまでの一面のある日を遊ぶ。取れなければ一覧に留まる（何も壊さない）。 */
function playArchive(date: string): void {
  if (!isArchiveOpen(date, dailyDate, extrasOwned())) return;
  void fetchDaily(date).then((level) => {
    if (level === null) return;
    stageSelect.close();
    // 今日より後の日は「先の一面」。買った人だけがここに来る（isArchiveOpen が見ている）。
    const heading = date > dailyDate ? t.upcomingTitle : t.archiveTitle;
    playDailyLevel(level, `${heading}　${dateLabelOf(date)}`);
  });
}

/** 今日の一面を盤面に出す。 */
function loadDaily(): void {
  if (dailyLevel === null) return;
  // 盤面の題は「今日の一面　10月9日」。面の JSON の name は日本語の控えなので見せない。
  playDailyLevel(dailyLevel, `${t.dailyTitle}　${dateLabelOf(dailyDate)}`);
}

/**
 * 通水の褒賞を広告で増やす提案を、何面おきに出すか。
 *
 * **毎面出すと催促になる。** 静かな盤面を見つめる遊びなので、
 * 数面に一度、通り過ぎた区切りとして置くくらいがちょうどよい。
 */
const MULTIPLY_OFFER_EVERY = 5;

/**
 * 何面通したら評価を頼むか（本編）。
 *
 * **3面。** 2026-09-23 に 8面から下げた。
 *
 * 8面にしていたのは「良いか悪いか言えるところまで遊んでから」という理由で、
 * 考え方としては正しい。だが**実測で評価が全地域0件だった**（JP/US/KR/TW/GB）。
 * 8面まで来る人がそもそも居ないなら、丁寧さは誰にも届いていない。
 *
 * 3面は、視点を回すという核が伝わり切ったところ。まだ課金の話（第一章末＝12面）には
 * 触れていないので、**売り込みの隣で頼まない**という条件は変わらず満たす。
 *
 * **星の平均が下がる危険は承知の上。** 0件のいま、守るべき平均が無い。
 * 1件でも付くほうが、検索順位にもページの変換にも効く。
 */
const REVIEW_AFTER_MAIN_CLEARS = 3;

/** 提案を出してからの通水数。**この版の間だけ数える**（保存しない）。 */
let clearsSinceOffer = 0;

/** いま結果表示に「雫を増やす」を出してよいか。通水のたびに決め直す。 */
let multiplyOffered = false;
let prevPhase: GamePhase = game.state.phase;

function cancelAdvance(): void {
  if (advanceTimer === null) return;
  window.clearTimeout(advanceTimer);
  advanceTimer = null;
}

/**
 * レベルを読み込み、メッシュを組み直す。
 * 順序は MODULE-CONTRACTS §16.7 の通り。**レジストリを空にするのが先**で、
 * これを飛ばすと前のレベルの水チューブとジオメトリが解放されずに漏れる。
 */
function loadLevelAt(index: number): void {
  const base = LEVELS[index];
  if (base === undefined) return;
  loadLevel(base, index);
}

/** 面を盤面に出す。`index` は `LEVELS` の添字、日替わりなら -1。 */
function loadLevel(base: Level, index: number): void {
  // 二周目は手数と時間を締めた**写し**を読む。元の面は変えない
  // （同じ面を一周目でも遊ぶため）。日替わりは周回と無関係なので写さない。
  const level = loop >= 2 && index >= 0 ? toLoop2(base) : base;

  cancelAdvance();
  levelIndex = index;
  // 延長は面をまたがない（上の timeBonusMs のコメント参照）。
  timeBonusMs = 0;
  timeBonusCount = 0;

  clearMeshRegistry();
  scene.clearRoot();

  game.loadLevel(level); // ここで pieces が展開され、startView のグラフが組まれる
  rebuildMeshes();

  const bounds = computeBounds(game.state.pieces);
  scene.setBounds(bounds);
  rig.setBounds(bounds);
  rig.snapTo(level.startView);
  rig.unlockCamera();
  scene.resize();

  // 計測はまだ始めない。**最初に盤面を操作した瞬間**に始める（METAGAME-SPEC §1.1、
  // `startClockOnFirstMove`）。面を開いた瞬間からだと、盤面を眺めているだけで
  // 時間が進み、ブラウザ体験版では頁を開いた途端に時計が走っていた（ユーザーの指摘）。
  // 「やりなおす」はこの経路を通るのでタイマーもゼロ・未開始に戻る。
  // 「もういちど」（水を引くだけ）はここを通らないので計測が続く——それが狙い。
  timer.reset();
}

/**
 * 盤面への最初の操作で計測を始める。視点を回す・上げ下げする・放水・レバーの
 * どれでも始まる（ボタン・キー・スワイプ・レバーの絵、どの経路でも同じ）。
 * 端で無視される操作（真上でさらに上げる等）でも始める。押した瞬間から、が約束なので。
 * 2回目以降は何もしない。`idle` の間しか効かないので、時間切れで止めた時計を
 * ここから動かしてしまうことは無い。
 */
function startClockOnFirstMove(): void {
  if (timer.phase === 'idle') timer.start();
}

/**
 * ピースのメッシュを組み直す。レベルのロード時と**レバーを倒したとき**に呼ぶ。
 * レジストリを空にするのが先。飛ばすと前の水チューブとジオメトリが漏れる。
 */
function rebuildMeshes(): void {
  clearMeshRegistry();
  scene.clearRoot();
  for (const piece of game.state.pieces) {
    scene.root.add(buildPieceMesh(piece, game.state.leverOn));
  }
}

function scheduleAdvance(): void {
  cancelAdvance();
  // 区分（本編／エクストラ）をまたいで勝手に進めない。
  // 本編の最終面をクリアしたらそこで留まる（全面クリア画面を出す場所）。
  //
  // **解放されていない面へは送らない。** エクストラの1面目は試遊で開くので、
  // 素通りを許すと買わずに全部遊べてしまう（`nextPlayableIndex` の注記）。
  // 日替わりには「次の面」が無い。通したら一覧へ帰す。
  const next = levelIndex < 0 ? null : nextPlayableIndex(LEVELS, records, levelIndex, {
    loop,
    unlockAll: UNLOCK_ALL,
    demoLimit: DEMO_LIMIT,
  });
  advanceTimer = window.setTimeout(() => {
    advanceTimer = null;
    if (next !== null) {
      loadLevelAt(next);
      return;
    }
    // 行き先が無いなら盤面に置き去りにせず、一覧へ帰す。
    // **売り場への導線はそこにある。** 祝いの画面が出ているときは譲る。
    if (!finale.isOpen) stageSelect.open();
  }, clearHoldMs);
}

/**
 * この起動で一度でも通水したか。**評価を頼んでよいかの判定に使う。**
 * 起動直後にもステージ一覧は開くので、これが無いと立ち上げただけで頼んでしまう。
 */
let clearedThisSession = false;

/**
 * クリアを記録する。**`clear` に入った最初の1回だけ**呼ぶこと。
 * 状態機械は `clear` の間ずっと update を回すので、二重計上しやすい。
 */
function commitClear(): void {
  timer.stop();
  clearedThisSession = true;
  const level = currentLevel();
  if (level === undefined) return;

  // **祝いの画面は「初めて到達した瞬間」だけ出す。**
  // 到達済みかどうかで判定していたため、一周した後に1面を遊び直すたびに
  // 踏破の画面が被さっていた。祝いは出来事であって状態ではない。
  // 記録を更新する**前**に控えておく必要がある（更新後は両方 true になる）。
  const wasAllCleared = records.progress.allClearedAt !== null;
  // **全通水の褒美はエクストラから二周目へ移った。** 旧い旗（unlockedExtra）は
  // もう立たないので、それを見張っていると祝いの画面が毎回出る。
  const wasLoop2Unlocked = records.progress.unlockedLoop2;
  const wasFirstClear = records.levels[recordKey(level.id)] === undefined;

  records = recordClear(
    records,
    recordKey(level.id),
    timer.elapsedMs,
    game.state.rotations,
    MAIN_IDS,
    new Date(),
  );
  // 保存に失敗しても遊びは続く（METAGAME-SPEC §2.2）。戻り値は捨ててよい。
  saveRecords(records);

  // 雫を貯める。初回かどうかは**記録を更新する前**の状態で決める必要があるので、
  // 上で控えた wasFirstClear を使う。
  const prize = clearReward(wasFirstClear);
  setWallet(earn(wallet, prize));

  // ## 広告で褒賞を増やす提案
  // **無料で遊べる範囲だけ**に出す。エクストラは買った人しか遊べないので、
  // そこで広告を見せるのは二重取りになる（`extendOffer` と同じ理由）。
  // 数面に一度なのは、毎面出すと催促になるから。
  multiplyOffered = false;
  if (categoryOf(level.id) === 'main' && isRewardedAdAvailable()) {
    clearsSinceOffer += 1;
    if (clearsSinceOffer >= MULTIPLY_OFFER_EVERY) {
      clearsSinceOffer = 0;
      multiplyOffered = true;
      lastPrize = prize;
    }
  }

  // 全25面を通したら踏破の画面（METAGAME-SPEC §5.6）。
  // **今このクリアで初めて揃ったときだけ。** 称号を刻むのも同時に一度だけ。
  if (!wasAllCleared && isAllComplete(LEVELS, records)) {
    // 称号の証拠を先に刻む。画面より記録が先——ここで落ちても称号は残る。
    records = markAllCleared(records, new Date());
    saveRecords(records);
    cancelAdvance();
    setWallet(earn(wallet, DROPS_ALL_COMPLETE));
    lastPrize = DROPS_ALL_COMPLETE;
    window.setTimeout(() => {
      settings.setOpenButtonVisible(false);
      finale.show(LEVELS, records, 'all', DROPS_ALL_COMPLETE);
    }, clearHoldMs);
    return;
  }

  // 本編を全部通したら全面クリア画面へ（METAGAME-SPEC §4）。
  // こちらも**初めてエクストラが開いた瞬間だけ**。解放の通知が主題なので、
  // 二度目に出しても伝えることが無い。
  if (
    !wasLoop2Unlocked &&
    categoryOf(level.id) === 'main' &&
    isMainComplete(LEVELS, records)
  ) {
    cancelAdvance();
    // クリア演出（水がアクセント色になる）を見せてから被せる。
    setWallet(earn(wallet, DROPS_MAIN_COMPLETE));
    lastPrize = DROPS_MAIN_COMPLETE;
    window.setTimeout(() => {
      settings.setOpenButtonVisible(false);
      finale.show(LEVELS, records, 'main', DROPS_MAIN_COMPLETE);
    }, clearHoldMs);
  }
}

/** phase が変わった瞬間にだけ走る副作用（演出・カメラロック・面送り）。 */
function handlePhaseChange(from: GamePhase, to: GamePhase): void {
  if (from === to) return;

  // CORE-SPEC §3.1「flowing 中はカメラをロックする」。
  // state.ts も idle 以外の入力を弾くので、これは二重の保険である。
  if (to === 'flowing' || to === 'clear' || to === 'failed') rig.lockCamera();
  else rig.unlockCamera();

  switch (to) {
    case 'rotating':
      playSfx('rotate');
      break;
    case 'flowing':
      playSfx('pour');
      // 流れている間ずっと鳴らす。止めるのは flowing を抜けるとき（下の3箇所）。
      startFlow();
      break;
    case 'clear': {
      // クリア演出（水をアクセント色に）は main の責務。
      // state.ts は FlowDriver に setClear を要求しないので自分では呼ばない。
      water.setClear(true);
      stopFlow();
      playSfx('clear');
      // 初めての通水かどうかは、記録を書き換える**前**に控える（commitClear の注記と同じ理由）。
      const cleared = currentLevel();
      const firstClear =
        cleared !== undefined && records.levels[recordKey(cleared.id)] === undefined;
      // 待ち時間は commitClear が祝いの画面を予約する**前**に決めておく。
      clearHoldMs = shouldReveal(firstClear) && rig.reveal(revealOffsetForNow())
        ? REVEAL_TOTAL_MS + REVEAL_SETTLE_MS
        : CLEAR_ADVANCE_MS;
      // handlePhaseChange は遷移の瞬間にしか呼ばれないので、記録は一度きり。
      commitClear();
      // **提案を出している間は自動で進めない。** 進んでしまうと、
      // 押す間もなくボタンが消える。出口は「つぎへ」が受け持つ。
      if (!multiplyOffered) scheduleAdvance();
      break;
    }
    case 'failed':
      stopFlow();
      // 行き止まりでこぼれたときは水がぶつかる音を先に置く。
      // 時間切れと手数切れは水が動いていないので鈍い一撃だけにする。
      if (!game.state.timedOut && game.state.flow !== null) playSfx('splash');
      playSfx('fail');
      break;
    case 'idle':
      stopFlow();
      cancelAdvance();
      // 「もういちど」で水を引いたとき。種明かしの途中なら解いた視点へ戻す。
      rig.cancelReveal();
      break;
  }
}

// =============================================================================
// UI
// =============================================================================

/**
 * ボタンからフォーカスを外す。
 * 残したままだと Space キーがフォーカス中のボタンを再度叩いてしまい、
 * 「放水のつもりが やりなおす」といった取り違えが起きる。
 */
function blurActive(): void {
  const el = document.activeElement;
  if (el instanceof HTMLElement) el.blur();
}

/**
 * 失敗表示から水を引いて、見えている視点のまま組み直せる状態に戻す。
 * 面は読み直さないので、回転のやり直しにならない。
 */
function requestAgain(): void {
  game.reset();
}

function requestRotate(dir: -1 | 1): void {
  startClockOnFirstMove();
  // 失敗中でも押せる。水を引く処理は state.ts の rotate() が持っている。
  game.rotate(dir);
}

/** 仰角を1段。`+1` で真上へ近づく。端では state.ts 側が黙って無視する。 */
function requestElevate(dir: -1 | 1): void {
  startClockOnFirstMove();
  game.elevate(dir);
}

function requestPour(): void {
  startClockOnFirstMove();
  game.pour();
}

/** レバーを倒す。動いたピースを反映するためメッシュを組み直す。 */
function requestLever(): void {
  startClockOnFirstMove();
  if (!game.toggleLever()) return;
  // 石橋が動く音。視点を回す音より重く長い（一手ぶんの重みを音でも出す）。
  playSfx('lever');
  rebuildMeshes();
}

/** 現在の面をやり直す。`game.retry()` と違い、面送りタイマも確実に止める。 */
function requestRetry(): void {
  if (levelIndex < 0) {
    // 盤面にいま出ている題（今日／これまで）をそのまま使って読み直す。
    if (activeDaily !== null) loadLevel({ ...activeDaily, name: game.state.level.name }, -1);
  } else loadLevelAt(levelIndex);
}

const hud = createHud(uiRoot, {
  onRotate: (dir) => {
    blurActive();
    requestRotate(dir);
  },
  onElevate: (dir) => {
    blurActive();
    requestElevate(dir);
  },
  onLever: () => {
    blurActive();
    requestLever();
  },
  onPour: () => {
    blurActive();
    requestPour();
  },
  onRetry: () => {
    blurActive();
    requestRetry();
  },
  onAgain: () => {
    blurActive();
    requestAgain();
  },
  onExtendTime: () => void extendTime(),
  // 可否と文言は**そのつど**訊かれる。資金源が広告か雫かで変わるため。
  canExtendTime: () => extendOfferLabel() !== null,
  extendTimeLabel: () => extendOfferLabel() ?? '',
  // 「つぎへ」は自動進行を止めているときだけ出す。
  onNext: () => {
    blurActive();
    multiplyOffered = false;
    advanceNow();
  },
  canNext: () => multiplyOffered,
  onMultiplyPrize: () => void multiplyPrize(),
  canMultiplyPrize: () => multiplyOffered,
  multiplyPrizeLabel: () => t.multiplyByAd(AD_MULTIPLIER),
  onShare: () => void sendShareCard(),
  canShare: () => isShareAvailable(),
});

/**
 * 通した一枚を送り出す。
 *
 * ## 盤面を写す手順に気をつける
 * `WebGLRenderer` は `preserveDrawingBuffer` を立てていないので、
 * **描いた直後の同じ実行単位でしか読み出せない。**
 * 間に `await` を挟むと合成が済んで真っ黒が返る。
 * だから `render()` と `toDataURL()` を**続けて**呼ぶ。
 *
 * ## 自動進行を止める
 * 送信面が出ている間に次の面が読み込まれると、閉じたときに別の盤面が
 * 現れることになる。押した時点で止め、終わったら一覧へ帰す。
 */
async function sendShareCard(): Promise<void> {
  blurActive();
  cancelAdvance();
  // **送るのは解いた視点の絵。** 種明かしの途中で押されても、ずれた絵を送らない。
  rig.cancelReveal();
  const level = currentLevel();
  if (level === undefined) return;

  scene.render();
  let boardPng: string;
  try {
    boardPng = scene.canvas.toDataURL('image/png');
  } catch {
    return; // 読み出せない環境。送れないだけ。
  }

  const number = LEVELS.slice(0, levelIndex + 1).filter(
    (lv) => categoryOf(lv.id) === categoryOf(level.id),
  ).length;
  // 日替わりは通し番号を持たない。「今日の一面　10月9日」と名乗る。
  const title = levelIndex < 0
    ? game.state.level.name
    : `${stageNumber(number)} — ${levelName(level.id, level.name)}`;
  const best = records.levels[recordKey(level.id)]?.bestTimeMs ?? null;
  const time = best === null ? null : formatTime(best);

  const png = await buildShareCard({ boardPng, title, time });
  if (png === null) return;
  await shareCard(shareText(title, time), png);
}

/**
 * いま次の面へ送る。自動進行を止めたあとの出口。
 *
 * **押して何も起きない出口を作らない。** 次が無い（区分の終わり、または
 * 試遊の終わりで閉じている）ときは一覧へ帰す。
 */
function advanceNow(): void {
  cancelAdvance();
  const next = levelIndex < 0 ? null : nextPlayableIndex(LEVELS, records, levelIndex, {
    loop,
    unlockAll: UNLOCK_ALL,
    demoLimit: DEMO_LIMIT,
  });
  if (next !== null) {
    loadLevelAt(next);
    return;
  }
  if (!finale.isOpen) stageSelect.open();
}

/**
 * 通水の褒賞を広告で増やす。**最後まで見たときだけ**渡す。
 *
 * 見ても見なくても、終わったら次の面へ送る。**断った人を留め置かない。**
 */
async function multiplyPrize(): Promise<void> {
  blurActive();
  if (!multiplyOffered) return;
  multiplyOffered = false;
  const watched = await showRewardedAd();
  // 褒賞はすでに渡してあるので、足すのは (倍率 - 1) 回ぶん。
  if (watched) setWallet(earn(wallet, lastPrize * (AD_MULTIPLIER - 1)));
  advanceNow();
}

/**
 * 広告を見せて制限時間を延ばす。
 *
 * 見返りを渡すのは**最後まで見られたときだけ**。読み込めなかった・途中で閉じた
 * ときは何も起きずに時間切れの表示のままにする（詰ませないこと、が唯一の要件）。
 */
async function extendTime(): Promise<void> {
  blurActive();
  if (!game.state.timedOut) return;

  // **押されたボタンに書いてあった払い方をそのまま実行する。**
  // ここで判断し直すと、表示と実行が食い違いうる（`extendOffer` の注記）。
  const offer = extendOffer();
  if (offer === null) return;

  if (offer === 'ad') {
    const watched = await showRewardedAd();
    // 最後まで見なかった。何も起きず、時間切れの表示のまま。詰ませない。
    if (!watched) return;
  } else {
    // **広告が出ない場所でも雫で延ばせる。** ここが無いと、エクストラは
    // 時間切れで手詰まりになる（買った人ほど行き止まりに当たることになる）。
    const paid = spend(wallet, COST_EXTEND_TIME);
    if (paid === null) return;
    setWallet(paid);
  }

  timeBonusMs += REWARD_TIME_MS;
  timeBonusCount += 1;
  game.resumeFromTimeUp();
  // 延ばしただけでは時計は動かない。止めた側が自分で戻す。
  timer.resume();
}

// `createHud` は `#ui` を replaceChildren で作り直すので、必ずその**後**に足す。
const stageSelect = createStageSelect(uiRoot, {
  getState: () => ({
    levels: LEVELS, records, unlockAll: UNLOCK_ALL, loop, demoLimit: DEMO_LIMIT,
    daily: dailyView(),
  }),
  onPlayDaily: () => {
    stageSelect.close();
    loadDaily();
  },
  onPlayArchive: (date: string) => playArchive(date),
  onLoopChange: (next: number) => {
    loop = next;
  },
  // ステージ一覧からも売り場へ行けるようにする。**設定の奥だけでは見つからない。**
  onOpenStore: () => {
    settings.open();
  },
  onOpenSettings: () => {
    settings.open();
  },
  canOpenStore: () => isStoreAvailable(),
  // 体験版だけ。続きは App Store で。
  appStoreUrl: IS_WEB_DEMO ? APP_STORE_URL : undefined,
  demoLimit: DEMO_LIMIT,
  onSelect: (index) => {
    stageSelect.close();
    loadLevelAt(index);
  },
  // 遊んでいる最中にステージを選び直せる入口は残す（右上）。
  // 設定の入口は左上に分けてある。
  withOpenButton: true,
  onVisibilityChange: (open) => {
    settings.setOpenButtonVisible(!open);
    // 一覧を開くたびに、日付が変わっていないか・取れなかった面が取れるかを確かめる。
    if (open) loadDailyIfNeeded();
    // ## 評価を頼む場所（`game/review.ts` の規則2）
    // **盤面の上では頼まない。** 時計が動いている最中に被せると、
    // 読んでいる間に時間が減る。一覧は時計も演出も動いていない。
    // 起動直後の一覧では頼まない——遊ぶ前の人に訊いても意味が無い。
    if (!open || !clearedThisSession) return;
    if (mainProgress(LEVELS, records).cleared < REVIEW_AFTER_MAIN_CLEARS) return;
    void requestReview('earlyCleared');
  },
});

const settings = createSettings(uiRoot, {
  getAudio: () => audioSettings(),
  // 動かしている最中は音にだけ反映する。耳で合わせられないと調整できない。
  onAudioChange: (next) => applyVolumes(next),
  // 指が離れたときだけ保存する。動かす途中で毎回書くと localStorage を叩きすぎる。
  onAudioCommit: (next) => saveAudioSettings(next),
  onOpenStages: () => stageSelect.open(),

  getSkinState: () => ({ skins, drops: wallet.drops }),

  /**
   * 配色を選ぶ。持っていなければ雫で買ってから着る。
   * **買えなければ何も起きない**（残高不足のボタンは押せない作りだが、二重の保険）。
   */
  onPickSkin: (id: string): boolean => {
    if (!skins.owned.includes(id)) {
      const skin = skinById(id);
      const paid = spend(wallet, skin.cost);
      if (paid === null) return false;
      setWallet(paid);
      skins = grantSkin(skins, id);
    }
    skins = selectSkin(skins, id);
    saveSkins(skins);
    applySkin();
    return true;
  },

  // **可否はそのつど訊かれる。** 課金の登録は非同期で後から済むので、
  // ここで一度だけ判定すると売り場が永久に出ない。
  canPurchase: () => isStoreAvailable(),
  /**
   * 「購入を復元」。**押されたときに走らせる**——起動時の自動復元だけでは
   * ガイドライン 3.1.1 を満たさない（実際にそれで審査に落ちた）。
   *
   * 端末を変えた人・入れ直した人は、買った事実が手元の記録に無い。
   * `Transaction.currentEntitlements` が唯一の正なので、そこから戻す。
   */
  onRestore: async (): Promise<number> => {
    const owned = await restore();
    let restored = 0;
    if (owned.includes(EXTRAS_PRODUCT_ID)) {
      if (!records.progress.extrasOwned) {
        records = grantExtras(records);
        saveRecords(records);
        if (stageSelect.isOpen) stageSelect.open();
      }
      setAdsRemoved(true);
      restored += 1;
    }
    if (owned.includes(NOADS_PRODUCT_ID)) {
      setAdsRemoved(true);
      restored += 1;
    }
    return restored;
  },
  onPurchase: async (productId: string): Promise<boolean> => {
    const okay = await purchase(productId);
    if (!okay) return false; // 取り消し・失敗。何も渡さず、遊びは続く
    const product = productById(productId);
    if (product === undefined) return false;
    if (product.kind === 'consumable') {
      setWallet(earn(wallet, product.drops));
    } else if (productId === EXTRAS_PRODUCT_ID) {
      records = grantExtras(records);
      saveRecords(records);
      // **エクストラを買うと広告も外れる。** 単体で売るのをやめたぶん、
      // こちらに含めてある（商品名にも「広告なし」と書いてある）。
      setAdsRemoved(true);
      // 買った瞬間にエクストラを選べるようにする。開いたままの一覧を組み直す。
      if (stageSelect.isOpen) stageSelect.open();
    } else {
      setAdsRemoved(true);
    }
    return true;
  },
  onTestSound: () => {
    // 解錠は利用者の操作の中でしか通らない。このボタンはまさにその中なので、
    // ここで呼び直すのが最も確実な復帰口になる。
    unlockAudio();
    onceRunning(() => {
      startBgm();
      playSfx('clear');
    });
  },
  getAudioStatus: () => audioStateLabel(),
  // ブラウザでは開けないので、口ごと渡さない（節が出ない）。
  onOpenStoreLink: canOpenStore()
    ? (which) => void openStore(which === 'review' ? WRITE_REVIEW_URL : DEVELOPER_URL)
    : undefined,
});

/**
 * つまむ動作での拡大を止める。
 *
 * viewport の `user-scalable=no` で塞いだうえで、**WebKit 独自の
 * `gesture*` も止める。** viewport だけだと版によってはつまみ拡大が通り、
 * 一度拡大すると戻す手立てが無い（盤面が画面外へ出て遊べなくなる）。
 *
 * `passive: false` を明示しないと preventDefault が効かない。
 */
for (const type of ['gesturestart', 'gesturechange', 'gestureend']) {
  document.addEventListener(type, (event) => event.preventDefault(), { passive: false });
}

/**
 * ダブルタップでの拡大を止める最後の砦。
 *
 * `touch-action: manipulation` と viewport で塞いでいるが、
 * **実機で実際に拡大して戻らなくなった**ため、二重に押さえる。
 * 300ms 以内の2度目のタップを飲み込む。
 */
let lastTouchEnd = 0;
document.addEventListener(
  'touchend',
  (event) => {
    const now = Date.now();
    if (now - lastTouchEnd <= 300) event.preventDefault();
    lastTouchEnd = now;
  },
  { passive: false },
);

// =============================================================================
// 音
// =============================================================================

/**
 * 操作のたびに音を起こそうとする。
 *
 * **iOS では利用者の操作の中でしか AudioContext を起こせない。**
 * 起動時に作っても suspended のまま黙り続ける。
 *
 * 最初は最初の1回だけ試して外していたが、それだと**失敗したときに二度目が無い**。
 * 実機で「音が全く鳴らない」となったので、走り出すまで毎回試す形に変えた。
 * 走り出したあとは即座に return するので費用はほぼ無い。
 *
 * `resume()` は非同期なので、BGM は `onceRunning` で待ってから始める。
 * 待たずに始めると、まだ動いていない時計の上に音を並べることになる。
 */
function bootAudio(): void {
  if (isAudioRunning()) return;
  unlockAudio();
  onceRunning(startBgm);
}

// pointerdown だけでなく touchend / click / keydown も見る。
// iOS の版によって「操作の中」と認められる種類が違うため、網を広くとる。
for (const type of ['pointerdown', 'touchend', 'click', 'keydown'] as const) {
  window.addEventListener(type, bootAudio, { capture: true, passive: true });
}

document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    // 裏に回ったら黙る。BGM は残したまま context を止めるだけなので、
    // 戻ったときに同じ場の音が続く（作り直すと立ち上がりが鳴り直して耳につく）。
    suspendAudio();
    return;
  }
  unlockAudio();
  // 閉じている間の時間回復をここで精算する。起動時だけだと、
  // 開きっぱなしにしている人にいつまでも入らない。
  setWallet(settleRecovery(wallet, new Date()));
});

const finale = createFinale(uiRoot, {
  // 本編を全通水した人が次に行く先。エクストラは課金解放になったので、
  // ここは**二周目**へ送る（払わない人にも次が残るようにする）。
  onExtra: () => {
    loop = 2;
    finale.leave(() => stageSelect.open());
    void requestReview('mainComplete');
  },
  // **祝いを閉じたら盤面ではなく「はじまり」へ帰す。**
  // 起動時に開くステージ選択が家であり、通り終えた面に戻しても行き場が無い。
  // `leave` は溶暗しながら閉じ、その始めにここを呼ぶので、
  // 消えていく間ずっと帰り先が下に見えている。
  onClose: () => {
    finale.leave(() => stageSelect.open());
    // 祝いを**読み終えてから**頼む。重ねると祝いが読めない（規則3）。
    void requestReview('mainComplete');
  },
  /**
   * エクストラへの誘い。**祝いの画面でいちばん大事な口。**
   *
   * 買っていない人にだけ出す。試遊がまだなら「試す」、済んでいれば「開く」。
   * **表示のたびに決め直す**——買った直後に開き直したときに売り文句が
   * 残っていてはいけない。
   */
  extraInviteLabel: (): string | null => {
    if (records.progress.extrasOwned || records.progress.unlockedExtra) return null;
    const freeId = freeExtraId(LEVELS);
    if (freeId === null) return null;
    return records.levels[freeId] === undefined
      ? t.inviteTry
      : t.inviteOpen;
  },
  onExtraInvite: () => {
    const freeId = freeExtraId(LEVELS);
    if (freeId === null) return;
    // まだ試していないなら、その面へ直接送る。**買わせる前に触らせる。**
    if (records.levels[freeId] === undefined) {
      const index = LEVELS.findIndex((lv) => lv.id === freeId);
      if (index >= 0) {
        finale.leave(() => {
          settings.setOpenButtonVisible(true);
          loadLevelAt(index);
        });
        return;
      }
    }
    // 試し終えている人には売り場を開く。
    finale.leave(() => {
      stageSelect.open();
      settings.open();
    });
  },
  // 提供者が居るときだけ渡す。居なければ祝いの画面にボタン自体が出ない。
  ...(isRewardedAdAvailable()
    ? {
        onMultiplyPrize: async (): Promise<number | null> => {
          const watched = await showRewardedAd();
          // 最後まで見なかったら**取り分はそのまま**。詫びも責めもしない。
          if (!watched) return null;
          // 褒賞はすでに渡してあるので、足すのは (倍率 - 1) 回ぶん。
          setWallet(earn(wallet, lastPrize * (AD_MULTIPLIER - 1)));
          return lastPrize * AD_MULTIPLIER;
        },
      }
    : {}),
});

/**
 * 「時を延ばす」の払い方。出さないなら null。
 *
 * **ここが唯一の判断箇所。** 表示と実行で別々に決めてはいけない。
 * 以前は `extendOfferLabel()` と `extendTime()` が独立に判断していて、
 * 条件が食い違った瞬間に**ボタンが嘘をつく**（「雫で延ばす」と書きながら
 * 広告を流し、雫を引かない）。二度と分けないこと。
 *
 * 資金源が2つある——広告と雫。**広告があるならそちらを先に出す**。
 * 無料で済むほうを黙って隠して雫を使わせるのは、売り方として不誠実である。
 *
 * ## エクストラでは広告を出さない
 * **エクストラは買った人しか遊べない。** 代金を払った先で広告を見せるのは
 * 二重取りであり、買ったことへの裏切りになる。エクストラでは雫だけを出す
 * （雫は遊んで貯めたものなので、払った人自身の持ち物である）。
 *
 * 広告が出るのは**無料で遊べる範囲**だけ——本編の二周目と、祝いの画面の
 * 「5倍」。`setAdsRemoved()` はそのうえで、広告を消す買い切りをした人から
 * さらに取り除く。
 */
type ExtendOffer = 'ad' | 'drops';

function extendOffer(): ExtendOffer | null {
  if (timeBonusCount >= REWARD_MAX_PER_LEVEL) return null;
  const paidContent = categoryOf(game.state.level.id) === 'extra';
  if (!paidContent && isRewardedAdAvailable()) return 'ad';
  if (wallet.drops >= COST_EXTEND_TIME) return 'drops';
  return null;
}

function extendOfferLabel(): string | null {
  const offer = extendOffer();
  if (offer === 'ad') return t.extendByAd;
  if (offer === 'drops') return t.extendByDrops(COST_EXTEND_TIME);
  return null;
}

/** 直前に祝いの画面で渡した褒賞。広告で倍にするときの基準になる。 */
let lastPrize = 0;

const debug = isDebugEnabled() ? createDebugOverlay(uiRoot, scene.camera, scene.canvas) : null;

game.subscribe((state: GameState) => {
  hud.update(state);
  debug?.update(state);
  const from = prevPhase;
  prevPhase = state.phase;
  handlePhaseChange(from, state.phase);
});

// =============================================================================
// 入力（CORE-SPEC §5 / MODULE-CONTRACTS §16.8）
// =============================================================================

window.addEventListener('keydown', (event: KeyboardEvent) => {
  if (event.repeat) return; // 押しっぱなしで連続回転させない
  if (event.altKey || event.ctrlKey || event.metaKey) return;
  // 種明かしの途中なら、そのキーは「飛ばす」にだけ使う。
  if (skipReveal()) {
    event.preventDefault();
    return;
  }

  // ステージ選択が開いている間は盤面の操作を通さない。
  // Escape での閉じるは stageSelect 自身が拾う。
  if (stageSelect.isOpen || finale.isOpen || settings.isOpen) return;

  if (event.key === 'l' || event.key === 'L') {
    event.preventDefault();
    requestLever();
    return;
  }

  if (event.key === 's' || event.key === 'S') {
    event.preventDefault();
    stageSelect.toggle();
    return;
  }

  switch (event.key) {
    case 'ArrowRight':
      event.preventDefault();
      requestRotate(1);
      break;
    case 'ArrowLeft':
      event.preventDefault();
      requestRotate(-1);
      break;
    case 'ArrowUp':
      event.preventDefault();
      requestElevate(1);
      break;
    case 'ArrowDown':
      event.preventDefault();
      requestElevate(-1);
      break;
    case ' ':
    case 'Spacebar': // 古い Edge / Firefox
      event.preventDefault(); // ページスクロールとフォーカス中ボタンの誤爆を防ぐ
      requestPour();
      break;
    case 'r':
    case 'R':
      event.preventDefault();
      requestRetry();
      break;
    default:
      break;
  }
});

// ---- 水平ドラッグ / スワイプでの回転 ----------------------------------------
// 1ジェスチャにつき1回だけ発火させる（指を大きく振っても2回転しない）。

let dragPointerId: number | null = null;
let dragStartX = 0;
let dragStartY = 0;
let dragFired = false;

scene.canvas.addEventListener('pointerdown', (event: PointerEvent) => {
  if (!event.isPrimary || stageSelect.isOpen || finale.isOpen || settings.isOpen) return;
  // 盤面に触れたら種明かしを飛ばす。その指で回転まで起こさない。
  if (skipReveal()) return;
  dragPointerId = event.pointerId;
  dragStartX = event.clientX;
  dragStartY = event.clientY;
  dragFired = false;
  scene.canvas.setPointerCapture(event.pointerId);
});

scene.canvas.addEventListener('pointermove', (event: PointerEvent) => {
  if (dragPointerId !== event.pointerId || dragFired) return;
  const dx = event.clientX - dragStartX;
  const dy = event.clientY - dragStartY;
  const ax = Math.abs(dx);
  const ay = Math.abs(dy);
  // 水平＝方位、垂直＝仰角。**優勢な方だけを採り、1ジェスチャにつき1回だけ発火する。**
  // 両方を同時に受け付けると方位と仰角が同時に動きかねない。それは
  // カメラ方向が球面上の2次元パッチを掃くことになり、遷移中のゴースト接続が
  // 爆発的に増える（PROJECTION-9VIEW §3.8.6）。状態機械も弾くが、入力段でも通さない。
  if (Math.max(ax, ay) < DRAG_THRESHOLD_PX) return;
  dragFired = true;
  if (ax > ay) requestRotate(dx > 0 ? 1 : -1);
  else requestElevate(dy < 0 ? 1 : -1); // 上へドラッグ＝視点を上げる
});

/**
 * 絵のレバーを直接叩けるようにする。
 *
 * ボタンだけだと、盤面のレバーが「見えているのに触れない飾り」になる。
 * 見えているものが触れることは、この作品の手触りとして自然である。
 *
 * **ドラッグと取り違えない。** 指を動かしていたら視点の操作なので拾わない
 * （`dragFired` と移動量の両方で見る）。
 */
const raycaster = new THREE.Raycaster();

/**
 * 指の太さぶんの余裕 (px)。
 *
 * レバーの駒は盤面では小さく、真ん中の一点しか当たらないと**指では狙えない**
 * （画面を 900 点走査して当たったのは1点だけだった）。
 * 見た目を大きくすると意匠が崩れるので、**当たり判定だけを広げる。**
 */
const LEVER_TOUCH_RADIUS_PX = 26;

/** その1点に駒があるか。手前から順に見て、最初に見つかった駒を返す。 */
function pieceAt(x: number, y: number): string | null {
  const rect = scene.canvas.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return null;
  const ndc = new THREE.Vector2(
    ((x - rect.left) / rect.width) * 2 - 1,
    -((y - rect.top) / rect.height) * 2 + 1,
  );
  raycaster.setFromCamera(ndc, scene.camera);
  for (const hit of raycaster.intersectObjects(scene.root.children, true)) {
    // メッシュ群は駒の id を名前に持つ（`buildPieceMesh`）。親をたどって探す。
    let node: THREE.Object3D | null = hit.object;
    while (node !== null && node !== scene.root) {
      const found = game.state.pieces.find((p) => p.id === node?.name);
      if (found !== undefined) return found.def.type;
      node = node.parent;
    }
  }
  return null;
}

function leverAtPointer(event: PointerEvent): boolean {
  if (!game.state.hasLever) return false;
  if (pieceAt(event.clientX, event.clientY) === 'lever') return true;
  // 中心を外していても、指の太さぶんの円内にレバーがあれば拾う。
  // **石や水路が先に当たっていても諦めない**——レバーは小さく、
  // 隣の柱に隠れがちなので、周囲を見ないと実質押せない。
  for (let i = 0; i < 8; i += 1) {
    const th = (i / 8) * Math.PI * 2;
    const x = event.clientX + Math.cos(th) * LEVER_TOUCH_RADIUS_PX;
    const y = event.clientY + Math.sin(th) * LEVER_TOUCH_RADIUS_PX;
    if (pieceAt(x, y) === 'lever') return true;
  }
  return false;
}

function endDrag(event: PointerEvent): void {
  if (dragPointerId !== event.pointerId) return;
  if (scene.canvas.hasPointerCapture(event.pointerId)) {
    scene.canvas.releasePointerCapture(event.pointerId);
  }
  // 指をほとんど動かしていない＝叩いた、とみなす。
  const moved = Math.hypot(event.clientX - dragStartX, event.clientY - dragStartY);
  if (!dragFired && moved < 12 && leverAtPointer(event)) {
    blurActive();
    requestLever();
  }
  dragPointerId = null;
}

scene.canvas.addEventListener('pointerup', endDrag);
scene.canvas.addEventListener('pointercancel', endDrag);

// ---- リサイズ ---------------------------------------------------------------
// scene.ts は自分でリスナを登録しない契約（§4.2）なので main が受け持つ。

window.addEventListener('resize', () => {
  scene.resize();
});

// =============================================================================
// メインループ
// =============================================================================

let lastTime = -1;

/**
 * 1フレーム分を進める。**rAF からも検証フックからも、必ずここを通す。**
 * ここを迂回して `game.update` だけ叩くと、タイマーだけ止まった偽の世界で
 * 検証してしまう（実際にそれで「タイムが常に 0」という誤った観測をした）。
 */
function step(dt: number): void {
  // game.update の中で rotation / flow のドライバが進む。
  // 接続グラフの再構築もこの中（回転完了フレーム）だけで起きる。
  game.update(dt);
  // 種明かしは状態機械の外で回す（cameraRig.updateReveal の注記）。
  rig.updateReveal(dt);

  // 計測は dt で進む。タブが非表示なら rAF ごと止まるので、
  // 放置した時間は自動的に計上されない（METAGAME-SPEC §1.2）。
  // ステージ選択を開いている間も止める——盤面を見ていないので思考時間ではない。
  // 設定を開いている間も止める。盤面を見ていないので思考時間ではない。
  if (!stageSelect.isOpen && !finale.isOpen && !settings.isOpen) timer.update(dt);

  // 制限時間の判定はここでしかできない。タイマーは「タブが隠れたら止まる」
  // 「ステージ選択を開いている間は止まる」という画面側の事情を含んでおり、
  // 状態機械はそれを知らない（METAGAME-SPEC §1.2 / §1.3）。
  const limit = effectiveTimeLimitMs();
  if (limit !== null && timer.elapsedMs >= limit) {
    // **stop ではなく pause。** stop は「記録が確定した」という意味で、
    // timer.resume() が二度と効かなくなる（後から記録を伸ばせないための封）。
    // 時間切れはまだ試行の途中なので、延長で再開できる pause でなければならない。
    if (game.timeUp()) timer.pause();
  }

  hud.setTime(timer.elapsedMs, limit);
}

function frame(now: number): void {
  window.requestAnimationFrame(frame);
  const dt = lastTime < 0 ? 0 : Math.min(Math.max(now - lastTime, 0), MAX_DT_MS);
  lastTime = now;
  step(dt);
  scene.render();
}

/**
 * 起動時に**盤面ではなくステージ選択を開く。**
 *
 * かつてはアイコンを押した瞬間に1面が始まっていたが、それだと
 *   - 続きから遊びたい人が、毎回1面を経由してステージを開くことになる
 *   - 全体が何面あるのかを見ないまま始まるので、遊びの規模が伝わらない
 * という二つの損がある。玄関を挟むほうが、続きにも初回にも素直である。
 *
 * 裏では「続き」の面を読み込んでおく。ステージ選択を閉じたときに
 * 何も無い空間が出るのを避けるためで、タイマーは選択中は止まっている
 * （step() が stageSelect.isOpen を見ている）ので記録は汚れない。
 */
function bootTarget(): number {
  const firstUncleared = LEVELS.findIndex(
    (lv) => categoryOf(lv.id) === 'main' && records.levels[lv.id] === undefined,
  );
  return firstUncleared >= 0 ? firstUncleared : 0;
}

/**
 * 課金と広告を差し込む。**起動時に一度だけ。**
 *
 * どちらも失敗しても投げない。差さらなければ入口が出ないだけで、
 * 遊びは最後の面まで成立する（rewardedAd.ts / iap.ts 規則1）。
 *
 * 非消耗品の持ち主は**毎回取り直す。** 端末を変えた人・入れ直した人は
 * 手元の記録に買った事実が無いので、`currentEntitlements` から戻す。
 * これをしないと買い直しを強いることになり、ガイドライン 3.1.1 に触れる。
 */
void (async (): Promise<void> => {
  // `?shop=1` で、買えない環境でも売り場を描かせる。**開発時のみ。**
  // 審査に出す課金商品には1つずつ「審査用スクリーンショット」が要り、
  // 売り場が映っていないと通らない。ところが売り場は StoreKit が商品を
  // 返したときしか描かないので、**実機で買える状態になるまで撮れない**
  // という順序の問題があった（商品は審査を通らないと買えるようにならない）。
  // ここで偽の提供者を差して、その堂々巡りを断つ。
  // 本番ビルドでは `import.meta.env.DEV` が false に畳まれて丸ごと消える。
  // `?ads=1` で、広告の提供者が居ない環境でも広告の入口を出す。**開発時のみ。**
  // 実物の AdMob は iOS でしか差さらないので、これが無いと
  // 「広告を見て雫を増やす」「時を延ばす」を机の上で一度も確かめられない。
  // `show()` は必ず true を返す——**見なかった場合の分岐はここでは試せない**ので、
  // そこは実機で確かめること。
  if (import.meta.env.DEV && new URLSearchParams(location.search).get('ads') === '1') {
    setRewardedAdProvider({
      isReady: () => true,
      show: async () => {
        await new Promise((r) => window.setTimeout(r, 400));
        return true;
      },
    });
  }
  if (import.meta.env.DEV && new URLSearchParams(location.search).get('shop') === '1') {
    setPurchaseProvider({
      isReady: () => true,
      priceOf: (id) => (productById(id) ? `¥${productById(id)!.yen}` : null),
      purchase: async () => false,
      restore: async () => [],
    });
    settings.refresh();
    return;
  }
  await registerStoreKit();
  const owned = await restoreOwned();
  if (owned.includes(EXTRAS_PRODUCT_ID) && !records.progress.extrasOwned) {
    records = grantExtras(records);
    saveRecords(records);
    if (stageSelect.isOpen) stageSelect.open();
  }
  // **名前ではなく id で見る。** 商品名は売り方の都合で変わる
  // （実際に「広告を消す」の単体販売をやめたときに変わった）。
  if (owned.includes(NOADS_PRODUCT_ID) || owned.includes(EXTRAS_PRODUCT_ID)) {
    setAdsRemoved(true);
  }
  await registerAdMob();
  // 差さった結果を設定画面へ反映する（売り場と広告の入口の出し分け）。
  if (settings.isOpen) settings.refresh();
})();

loadLevelAt(bootTarget());
// 今日の一面は起動と同時に取りに行く。一覧を開いたときに「読み込み中」で待たせない。
loadDailyIfNeeded();
// 起動時に一度描く。setWallet を通らないと表示が空のままだった。
hud.setDrops(wallet.drops);
// 保存されている配色を着せる。**盤面を組む前ではなく後**——
// マテリアルが未生成だと色だけ先に入って、次の生成で上書きされる。
applySkin();
// **`<html lang>` を実際の言語に揃える。** index.html は ja 固定なので、
// 英語で動かしていると読み上げも折り返しも日本語の規則で処理される。
document.documentElement.lang = LANG;
// 題名も揃える。ブラウザで開いたときの見出しになる。
document.title = LANG === 'ja' ? 'ESCHER FALLS — 落ちる水の城' : 'ESCHER FALLS';

/**
 * **体験版の初回だけ、玄関を通さずに1面から始める。**
 *
 * アプリでは玄関（ステージ選択）を挟むのが正しい——続きから遊ぶ人が毎回
 * 1面を経由せずに済み、全体の面数も伝わる（`bootTarget` の説明のとおり）。
 *
 * **外から来た人には、その理屈が当てはまらない。** リンクを押した人は
 * まだ何も遊んでおらず、続きも無い。最初に出るのが「?」の並んだ表だと、
 * この作品のいちばん強いところ——**不可能図形が実際に動く**という一点が
 * 一度も画面に出ないまま閉じられる。押した瞬間に盤面が立っているべきである。
 *
 * **一度でも通水した人には玄関を出す。** そこからは「続き」がある人だから。
 */
const skipEntrance = IS_WEB_DEMO && mainProgress(LEVELS, records).cleared === 0;
if (!skipEntrance) stageSelect.open();
window.requestAnimationFrame(frame);

// 検証用フック。docs/VERIFY-NOTES.md がこの存在を前提にしている
// （ブラウザペインのタブは hidden で rAF が止まるため、フレームを手で送る必要がある）。
// **開発時のみ。** 本番ビルドでは Vite が `import.meta.env.DEV` を false に畳んで丸ごと落とす。
if (import.meta.env.DEV) {
  Object.defineProperty(window, '__dbg', {
    value: {
      game,
      rig,
      water,
      scene,
      timer,
      stageSelect,
      finale,
      levels: LEVELS,
      loadLevelAt,
      levelIndexOf: () => levelIndex,
      /** 共有する一枚を組んで dataURL を返す。**送らない**（検証用）。 */
      buildShareCard: () => {
        scene.render();
        const level = LEVELS[levelIndex];
        return buildShareCard({
          boardPng: scene.canvas.toDataURL('image/png'),
          title: `${stageNumber(1)} — ${levelName(level.id, level.name)}`,
          time: formatTime(12345),
        });
      },
      records: () => records,
      settings,
      /**
       * 音。**検証では必ずここから触ること。**
       * `await import('/src/audio/engine.ts')` で取り直すと**別インスタンス**になり、
       * アプリ本体とは違う AudioContext を見てしまう（実際にそれで
       * 「解錠できていない」と誤診した）。VERIFY-NOTES §6・§7 を参照。
       */
      audio: {
        unlock: unlockAudio,
        isRunning: isAudioRunning,
        stateLabel: audioStateLabel,
        settings: audioSettings,
        applyVolumes,
        playSfx,
        startFlow,
        stopFlow,
        startBgm,
        seBus: () => seBusForDebug(),
        bgmBus: () => bgmBusForDebug(),
      },
      /**
       * 1フレーム進める。**検証ではこれを使うこと。**
       * `game.update` を直接叩くとタイマーが進まず、実物と挙動が食い違う。
       */
      step,
      /**
       * ストア用スクリーンショットの場面を組む。**開発時のみ。**
       *
       * `?shot=castle-flow` のように呼ぶと、ヘッドレス Chrome から
       * `tools/render-html.sh` で撮れる状態まで自動で進む。
       * **手で並べ直さないため**にここに置く——言語を切り替えて撮り直すたびに
       * 同じ手順を人がなぞると、6枚のどこかが必ずずれる。
       */
      shot: setUpShot,
    },
    configurable: true,
  });
  const want = new URLSearchParams(window.location.search).get('shot');
  if (want !== null) void setUpShot(want);
  // `?audit=1` で文字溢れを数える。結果は DOM に置くので
  // ヘッドレスの --dump-dom から読める。
  if (new URLSearchParams(window.location.search).get('audit') === '1') {
    void auditText().then((r) => {
      document.body.dataset.audit = r;
    });
  }
}

/**
 * 文字が枠から溢れていないかを機械的に数える。**開発時のみ。**
 *
 * ## なぜ目で見ないのか
 * 12言語 × 4画面を1枚ずつ見比べるのは当てにならない。実際、
 * ベトナム語のボタンが2行に折れているのを、6枚見た後にようやく見つけた。
 * **溢れは数えられる**ので、数える。
 *
 * 溢れの見方:
 * - `scrollWidth > clientWidth` … 枠の中に入りきっていない
 * - 画面の幅を超えている … 横にはみ出して切れている
 * - ボタンの高さが仲間より高い … 折り返して底が揃わなくなっている
 */
async function auditText(): Promise<string> {
  const idle = (ms: number): Promise<void> =>
    new Promise((r) => window.setTimeout(r, ms));
  const bad: string[] = [];
  const vw = window.innerWidth;
  let counted = 0;
  let langCounted = 0;

  function scan(where: string): void {
    for (const el of document.querySelectorAll<HTMLElement>('.btn')) {
      if (el.hidden || el.offsetParent === null) continue;
      const label = (el.textContent ?? '').trim().slice(0, 24);
      if (label === '') continue;

      // ## 折り返しの測り方
      // **`scrollWidth > clientWidth` では折り返しは見つからない。**
      // 折り返した要素は幅が変わらず、背だけ伸びる。
      // なので「1行に強制したときの幅」を測って、枠に収まるかを見る。
      // これは CSS 側で nowrap を掛けていても掛けていなくても同じ答えを出す
      // （掛けていれば「はみ出す」、掛けていなければ「折り返す」——どちらも駄目）。
      counted += 1;
      if (el.classList.contains('ef-lang-btn')) langCounted += 1;

      const keep = el.style.whiteSpace;
      el.style.whiteSpace = 'nowrap';
      const oneLine = el.scrollWidth;
      el.style.whiteSpace = keep;
      if (oneLine > el.clientWidth + 1) {
        bad.push(`${where}/1行に入らない「${label}」${oneLine}>${el.clientWidth}`);
      }

      const r = el.getBoundingClientRect();
      if (r.left < -1 || r.right > vw + 1) bad.push(`${where}/画面外「${label}」`);
    }
    // ## 並びの箱が溢れていないか
    // ボタンに nowrap を掛けると、折り返しの代わりに**箱からはみ出す。**
    // 折り返しだけを見ていると、直したつもりで別の壊れ方に移っただけになる。
    for (const row of document.querySelectorAll<HTMLElement>(
      '.hud-bottomcenter, .ef-actions, .ef-hud-side',
    )) {
      if (row.offsetParent === null) continue;
      if (row.scrollWidth > row.clientWidth + 1) {
        bad.push(`${where}/並びが箱に入らない ${row.className} ${row.scrollWidth}>${row.clientWidth}`);
      }
    }
    // 文字を出す欄そのものが溢れていないか（ボタン以外）。
    for (const el of document.querySelectorAll<HTMLElement>('.ef-shop-name, .ef-skin-name')) {
      if (el.offsetParent === null) continue;
      const r = el.getBoundingClientRect();
      if (r.right > vw + 1) bad.push(`${where}/画面外「${(el.textContent ?? '').slice(0, 20)}」`);
    }
  }

  stageSelect.close();
  settings.close();

  // ## 測る面を選ぶ
  // **既定の1面目にはレバーが無い。** そこで測っていたので、
  // 「放水」と「レバー」が横に2つ並ぶ形を一度も見ていなかった——
  // ベトナム語で折り返していたのは、まさにその並びだった。
  // 検査は**いちばん混む形**で行う。
  const leverIndex = LEVELS.findIndex((lv) => lv.id === '13-lever');
  if (leverIndex >= 0) loadLevelAt(leverIndex);
  await idle(500);
  scan('盤面（レバーあり）');

  settings.open();
  await idle(400);
  scan('設定');
  settings.close();
  stageSelect.open();
  await idle(400);
  scan('一覧');
  stageSelect.close();
  await idle(200);

  // **何個測ったかを必ず出す。** 0個でも「溢れなし」と言えてしまうので、
  // 「通った」と「見た」を区別できるようにしておく。
  const head = `測った ${counted} 個（うち言語 ${langCounted} 個）`;
  return bad.length === 0 ? `OK ${head}` : `${head} / ${bad.join(' / ')}`;
}

/**
 * 撮影用に盤面を組む。**開発時のみ**（`import.meta.env.DEV` の外からは呼ばれない）。
 *
 * 正解の向きは総当たりで探す。面ごとに解を書き並べると、
 * 面を差し替えたときに黙って古い向きのまま撮ることになる。
 */
async function setUpShot(name: string): Promise<void> {
  // **step() の dt はミリ秒。** 1/60 を渡すと 0.017ms しか進まず、
  // 回転が永遠に終わらないまま「なぜか違う絵」が撮れる（実際に撮れた）。
  const FRAME_MS = 1000 / 60;
  const idle = (ms: number): Promise<void> =>
    new Promise((r) => window.setTimeout(r, ms));
  const indexOf = (id: string): number => LEVELS.findIndex((lv) => lv.id === id);
  /**
   * 回転が終わるまで進める。
   * **固定回数では足りない。** 回転中は次の入力が弾かれるので、
   * 数え間違えるとその後の操作が全部無視され、黙って違う絵が撮れる。
   */
  const settle = (): void => {
    // **`rig.isRotating` では足りない。** カメラの補間が終わっても
    // 盤面の位相は `rotating` のままで、接続グラフはまだ組み直されていない。
    // そこで判定すると `goalReachable` を古い値のまま読む。
    for (let i = 0; i < 240; i += 1) {
      if (!rig.isRotating && game.state.phase !== 'rotating') break;
      step(FRAME_MS);
    }
    step(FRAME_MS);
  };
  /** 回して正解の向きへ。見つからなければそのまま。 */
  const seek = (): void => {
    for (let e = 0; e < 3; e += 1) {
      for (let a = 0; a < 4; a += 1) {
        if (game.state.goalReachable) return;
        game.rotate(1);
        settle();
      }
      if (!game.elevate(1)) return;
      settle();
    }
  };
  /** 放水して、水が見えるところまで進める。 */
  const pourUntil = (frames: number): void => {
    if (!game.pour()) return;
    for (let i = 0; i < frames; i += 1) step(FRAME_MS);
  };
  /** 放水して、**こぼれ落ちるところまで**進める。失敗の絵を撮るため。 */
  const pourUntilFailed = (): void => {
    if (!game.pour()) return;
    // **上限を欲張らない。** 同期ループで回している間は描画の隙が無いので、
    // 900 フレーム回すとヘッドレスの持ち時間を食い潰し、
    // **何も描かれないまま撮られる**（12言語ぶん真っ白を撮ってから気づいた）。
    // こぼれるのは放水から2〜3秒なので、400 フレームで足りる。
    for (let i = 0; i < 400; i += 1) {
      step(FRAME_MS);
      if (game.state.phase === 'failed') break;
    }
    // 落ちた粒が残っているうちに撮る。止まりきると跡が消える。
    for (let i = 0; i < 20; i += 1) step(FRAME_MS);
  };

  // **溶明・遷移を全部止める。** ヘッドレスの撮影は動きの途中で切り取るので、
  // 620ms の溶明が残っていると半透明の盤面が透けた半端な絵になる
  // （実際にそれで1枚無駄にした）。撮影のときだけ切る。
  const kill = document.createElement('style');
  kill.textContent = '*, *::before, *::after { animation: none !important; transition: none !important; }';
  document.head.append(kill);

  stageSelect.close();
  settings.close();

  switch (name) {
    case 'castle-idle':
      loadLevelAt(indexOf('08-castle'));
      await idle(300);
      game.rotate(1);
      settle();
      break;
    case 'castle-flow':
      loadLevelAt(indexOf('08-castle'));
      await idle(300);
      seek();
      pourUntil(150);
      break;
    case 'fail':
      loadLevelAt(indexOf('07-fork'));
      await idle(300);
      pourUntilFailed();
      break;
    case 'backflow':
      loadLevelAt(indexOf('x1-updraft'));
      await idle(300);
      seek();
      pourUntil(240);
      break;
    case 'lever':
      loadLevelAt(indexOf('13-lever'));
      await idle(300);
      game.toggleLever();
      settle();
      break;
    // ストア用ではなく**検証用。** 設定はいちばん長い文が入る画面なので、
    // 言語を増やすたびに溢れていないかを見る必要がある。
    case 'settings':
      loadLevelAt(0);
      await idle(300);
      settings.open();
      await idle(1500);
      break;
    case 'stage':
      loadLevelAt(0);
      await idle(300);
      stageSelect.open();
      // **開き終わるまで待つ。** 溶明の途中で撮ると、盤面が透けて重なった
      // 半端な絵になる（実際にそれで1枚無駄にした）。
      await idle(1500);
      break;
    default:
      return;
  }
  // ## WebGL を合成から外す
  // ヘッドレスの WebGL は SwiftShader（CPU 描画）で、**撮影の瞬間に
  // 合成が間に合わないことがある。** 落ちる場面は毎回違い、
  // 待ち時間を伸ばしても消えなかった（真っ白な絵が混ざり続けた）。
  //
  // そこで、描いた直後に静止画へ焼いて canvas と入れ替える。
  // 最後の画面に WebGL が居なくなるので、合成は確実に成る。
  // **`render()` と `toDataURL()` は続けて呼ぶこと**——間に await を挟むと
  // 合成が済んで真っ黒が返る（preserveDrawingBuffer を立てていない）。
  scene.render();
  try {
    const baked = document.createElement('img');
    baked.src = scene.canvas.toDataURL('image/png');
    baked.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;display:block';
    scene.canvas.replaceWith(baked);
    await new Promise<void>((r) => {
      if (baked.complete) r();
      else baked.onload = () => r();
    });
  } catch {
    // 焼けない環境では canvas のまま撮る。真っ白が混ざるかもしれないが、
    // 撮り直しの仕掛け（capture-raw.sh）が拾う。
  }
  await idle(200);
  // 撮れた場面が意図どおりかを **DOM から確かめられるように**残す。
  // ヘッドレスでは console が見えないので、失敗しても黙って違う絵が出る。
  document.body.dataset.shot =
    `${name} phase=${game.state.phase} reach=${String(game.state.goalReachable)} ` +
    `rot=${game.state.rotations}`;
}
